/*
 * File: GetCoursePolicy.ts
 * Author: Austin Robinson
 * Date Created: 10/1/2023
 * Copyright (c) DrivingDoc 2023
 */
import { createQuery } from 'react-query-kit';

import { PostGreSQLInterval, privateApi } from 'api';

export type GetCoursePolicyParams = {
  courseId: string;
};

export type GetCoursePolicyResult = {
  tenant_id: string;
  course_id: string;
  updated_at: string;
  min_time_to_complete: PostGreSQLInterval | null;
  max_time_to_complete: PostGreSQLInterval | null;
  allow_self_service_extensions: boolean | null;
  standard_extension_time: PostGreSQLInterval | null;
  standard_extension_fee: number | null;
  max_extensions: number | null;
  max_time_to_request_extension: PostGreSQLInterval | null;
  absences_allowed_before_fee: number | null;
  absence_fee: number | null;
  absence_fee_due: PostGreSQLInterval | null;
  absence_fee_due_lock: PostGreSQLInterval | null;
  no_permits_allowed_before_fee: number | null;
  no_permit_fee: number | null;
  no_permit_fee_due: PostGreSQLInterval | null;
  no_permit_fee_due_lock: PostGreSQLInterval | null;
  allow_cancellations: boolean | null;
  cancellations_allowed_before_fee: number | null;
  cancellation_fee: number | null;
  cancellation_fee_due: PostGreSQLInterval | null;
  cancellation_fee_due_lock: PostGreSQLInterval | null;
  late_cancellations_allowed_before_fee: number | null;
  late_cancellation_fee: number | null;
  late_cancellation_fee_due: PostGreSQLInterval | null;
  late_cancellation_fee_due_lock: PostGreSQLInterval | null;
  late_cancellation_deadline: PostGreSQLInterval | null;
  updated_by_user: {
    tenant_id: string;
    id: string;
    title: string | null;
    first_name: string | null;
    middle_name: string | null;
    last_name: string | null;
    nickname: string | null;
    email: string | null;
    date_of_birth: string | null;
    sex: string | null;
    active: boolean;
    created_at: string | null;
    updated_at: string | null;
    created_by: string | null;
    updated_by: string | null;
    des_id: string | null;
  };
};

async function getCoursePolicy(params: GetCoursePolicyParams): Promise<GetCoursePolicyResult> {
  // make api call
  const result = await privateApi.get(`${import.meta.env.VITE_API_BASE_URL}/courses/${params.courseId}/policy`, {});

  // return the data object
  return result.data as GetCoursePolicyResult;
}

const useGetCoursePolicy = createQuery<GetCoursePolicyResult, GetCoursePolicyParams, Error>({
  queryKey: ['course_policy'],
  fetcher: (variables) => getCoursePolicy(variables),
});

export { getCoursePolicy, useGetCoursePolicy };
